import { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams, useLocation } from 'react-router-dom';
import {
  Ticket,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  AlertCircle,
  Search,
  Filter,
  Building2,
  Settings,
  Save,
  XCircle,
  Plus,
} from 'lucide-react';
import api, { adminTicketsAPI, adminSettingsAPI } from '../../services/api';
import AdminNewTicketModal from '../../components/admin/AdminNewTicketModal';

const STORAGE_KEY = 'admin-tickets-filters';
const SETTINGS_KEY = 'tickets.defaultFilters';

const getSavedFilters = () => {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {}; } catch { return {}; }
};
const saveFilters = (filters) => {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(filters)); } catch {}
};

export default function AdminTicketsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const savedFilters = getSavedFilters();
  const [adminDefaults, setAdminDefaults] = useState(null);
  const [showDefaultMenu, setShowDefaultMenu] = useState(false);
  const [savingDefault, setSavingDefault] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const initialLoadDone = useRef(false);
  const lastRefreshTimestamp = useRef(null);

  // `defaults` is passed on first load: the state set a line earlier is not
  // visible to this closure yet, so the saved defaults never applied.
  const getInitialValue = (param, def = '', defaults = adminDefaults) => {
    const urlValue = searchParams.get(param);
    if (urlValue !== null) return urlValue;
    if (savedFilters[param] !== undefined) return savedFilters[param];
    if (defaults?.[param] !== undefined) return defaults[param];
    return def;
  };
  const [refreshTick, setRefreshTick] = useState(0);

  const [tickets, setTickets] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [boards, setBoards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewTicketModal, setShowNewTicketModal] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState('all');
  const [companyFilter, setCompanyFilter] = useState('');
  const [boardFilter, setBoardFilter] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 25,
    total: 0,
    pages: 0,
  });

  // Fetch companies, boards, and admin defaults on mount
  useEffect(() => {
    const loadInitial = async () => {
      try {
        const response = await api.get('/admin/companies?limit=500');
        setCompanies(response.data.companies || []);
      } catch (err) {
        console.error('Failed to fetch companies:', err);
      }
      try {
        const boardsResponse = await api.get('/admin/boards');
        setBoards(boardsResponse.data.boards || []);
      } catch (err) {
        console.error('Failed to fetch boards:', err);
      }
      let defaults = null;
      try {
        const res = await adminSettingsAPI.get(SETTINGS_KEY);
        if (res.data?.setting?.value) {
          defaults = res.data.setting.value;
          setAdminDefaults(defaults);
        }
      } catch {}
      // Initialize filters
      if (!initialLoadDone.current) {
        initialLoadDone.current = true;
        setSearch(getInitialValue('search', '', defaults));
        setStatusFilter(getInitialValue('status', 'all', defaults));
        setCompanyFilter(getInitialValue('company', '', defaults));
        setBoardFilter(getInitialValue('board', '', defaults));
        setIsInitialized(true);
      }
    };
    loadInitial();
  }, []);

  const fetchTickets = async (page = 1) => {
    setLoading(true);
    setError(null);

    try {
      const params = {
        page,
        pageSize: pagination.pageSize,
      };
      if (search) params.search = search;
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      if (companyFilter) params.companyId = companyFilter;
      if (boardFilter) params.boardId = boardFilter;

      const response = await adminTicketsAPI.list(params);
      setTickets(response.data.tickets);
      setPagination((prev) => ({
        ...prev,
        page,
        total: response.data.pagination.total,
        pages: response.data.pagination.pages,
      }));

      // Update URL params
      const newParams = new URLSearchParams();
      if (page > 1) newParams.set('page', page);
      if (search) newParams.set('search', search);
      if (statusFilter !== 'all') newParams.set('status', statusFilter);
      if (companyFilter) newParams.set('company', companyFilter);
      if (boardFilter) newParams.set('board', boardFilter);
      setSearchParams(newParams, { replace: true });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isInitialized) fetchTickets(1);
  }, [isInitialized, statusFilter, companyFilter, boardFilter, refreshTick]);

  // Auto-refresh when returning from detail page — restore all filters from URL
  // first so the refetch uses the returned-to state, not stale in-memory state.
  useEffect(() => {
    if (!isInitialized) return;
    const refreshTimestamp = location.state?.refresh;
    if (refreshTimestamp && refreshTimestamp !== lastRefreshTimestamp.current) {
      lastRefreshTimestamp.current = refreshTimestamp;
      setSearch(searchParams.get('search') || '');
      setStatusFilter(searchParams.get('status') || 'all');
      setCompanyFilter(searchParams.get('company') || '');
      setBoardFilter(searchParams.get('board') || '');
      // Unchanged filters would not re-run the fetch effect; the tick does.
      setRefreshTick((t) => t + 1);
    }
  }, [location.state?.refresh, isInitialized, searchParams]);

  const updateFilters = (updates) => {
    saveFilters({ status: updates.status ?? statusFilter, company: updates.company ?? companyFilter, board: updates.board ?? boardFilter });
  };

  const handleSaveAsDefault = async () => {
    setSavingDefault(true);
    try {
      const currentFilters = { status: statusFilter, company: companyFilter, board: boardFilter };
      await adminSettingsAPI.set(SETTINGS_KEY, currentFilters, 'Default filters for Admin Tickets page');
      setAdminDefaults(currentFilters);
      setShowDefaultMenu(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save the default filters');
    } finally { setSavingDefault(false); }
  };

  const handleClearLocalStorage = () => {
    localStorage.removeItem(STORAGE_KEY);
    setShowDefaultMenu(false);
  };

  useEffect(() => {
    if (!showDefaultMenu) return;
    const handleClickOutside = (e) => {
      if (!e.target.closest('.filter-settings-dropdown')) setShowDefaultMenu(false);
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showDefaultMenu]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchTickets(1);
  };

  const handlePageChange = (page) => {
    fetchTickets(page);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getStatusBadge = (ticket) => {
    const statusName = ticket.status?.name?.toLowerCase() || '';

    if (ticket.closedFlag || statusName.includes('closed')) {
      return (
        <span className="px-2 py-1 text-xs font-medium rounded-full bg-neutral-100 dark:bg-neutral-700 text-neutral-600 dark:text-neutral-300">
          {ticket.status?.name || 'Closed'}
        </span>
      );
    }
    if (statusName.includes('new')) {
      return (
        <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300">
          {ticket.status?.name || 'New'}
        </span>
      );
    }
    if (statusName.includes('progress') || statusName.includes('assigned')) {
      return (
        <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 dark:bg-yellow-900/50 text-yellow-700 dark:text-yellow-300">
          {ticket.status?.name || 'In Progress'}
        </span>
      );
    }
    return (
      <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300">
        {ticket.status?.name || 'Open'}
      </span>
    );
  };

  const getPriorityBadge = (ticket) => {
    const priorityId = ticket.priority?.id;
    const priorityName = ticket.priority?.name || '-';

    if (priorityId <= 2) {
      return (
        <span className="px-2 py-1 text-xs font-medium rounded bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300">
          {priorityName}
        </span>
      );
    }
    if (priorityId <= 4) {
      return (
        <span className="px-2 py-1 text-xs font-medium rounded bg-yellow-100 dark:bg-yellow-900/50 text-yellow-700 dark:text-yellow-300">
          {priorityName}
        </span>
      );
    }
    return (
      <span className="px-2 py-1 text-xs font-medium rounded bg-neutral-100 dark:bg-neutral-700 text-neutral-600 dark:text-neutral-300">
        {priorityName}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-neutral-900 dark:text-white">Tickets</h1>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
            View and search service tickets across all companies
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowNewTicketModal(true)}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-500 rounded-lg hover:bg-primary-600"
        >
          <Plus className="w-4 h-4" />
          New Ticket
        </button>
      </div>

      {showNewTicketModal && (
        <AdminNewTicketModal onClose={() => setShowNewTicketModal(false)} />
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <form onSubmit={handleSearch} className="flex gap-2 flex-1">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by ID, summary, or company..."
              className="w-full pl-10 pr-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-neutral-800 dark:text-white"
            />
          </div>
          <button
            type="submit"
            className="px-4 py-2 text-sm font-medium text-white bg-primary-500 rounded-lg hover:bg-primary-600"
          >
            Search
          </button>
        </form>

        <div className="flex items-center gap-2">
          <Filter className="w-5 h-5 text-neutral-400" />
          <select
            value={companyFilter}
            onChange={(e) => setCompanyFilter(e.target.value)}
            className="px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 max-w-[200px] bg-white dark:bg-neutral-800 dark:text-white"
          >
            <option value="">All Companies</option>
            {companies.map((company) => (
              <option key={company.cw_company_id} value={company.cw_company_id}>
                {company.name}
              </option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); updateFilters({ status: e.target.value }); }}
            className="px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-neutral-800 dark:text-white"
          >
            <option value="all">All Status</option>
            <option value="open">Open</option>
            <option value="closed">Closed</option>
          </select>
          <select
            value={boardFilter}
            onChange={(e) => { setBoardFilter(e.target.value); updateFilters({ board: e.target.value }); }}
            className="px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 max-w-[200px] bg-white dark:bg-neutral-800 dark:text-white"
          >
            <option value="">All Boards</option>
            {boards.map((board) => (
              <option key={board.id} value={board.id}>
                {board.name}
              </option>
            ))}
          </select>

          {/* Settings dropdown */}
          <div className="relative filter-settings-dropdown">
            <button
              onClick={() => setShowDefaultMenu(!showDefaultMenu)}
              className="p-2 text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg"
              title="Filter settings"
            >
              <Settings className="w-4 h-4" />
            </button>
            {showDefaultMenu && (
              <div className="absolute right-0 top-full mt-1 w-56 bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-lg shadow-lg z-10">
                <div className="p-2 border-b border-neutral-200 dark:border-neutral-700">
                  <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase">Filter Defaults</p>
                </div>
                <div className="p-1">
                  <button
                    onClick={handleSaveAsDefault}
                    disabled={savingDefault}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded"
                  >
                    <Save className="w-4 h-4" />
                    {savingDefault ? 'Saving...' : 'Save current as admin default'}
                  </button>
                  <button
                    onClick={handleClearLocalStorage}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded"
                  >
                    <XCircle className="w-4 h-4" />
                    Clear my saved preference
                  </button>
                </div>
                {adminDefaults && (
                  <div className="p-2 border-t border-neutral-200 dark:border-neutral-700 text-xs text-neutral-500 dark:text-neutral-400">
                    <p className="font-medium mb-1">Current admin defaults:</p>
                    <p>Status: {adminDefaults.status || 'All'}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-3 text-red-700 dark:text-red-300">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <p>{error}</p>
        </div>
      )}

      {/* Ticket Table */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : tickets.length === 0 ? (
        <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-12 text-center">
          <Ticket className="w-12 h-12 mx-auto mb-4 text-neutral-400 dark:text-neutral-500" />
          <p className="text-neutral-500 dark:text-neutral-400">No tickets found</p>
        </div>
      ) : (
        <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-neutral-50 dark:bg-neutral-700/50 border-b border-neutral-200 dark:border-neutral-700">
                  <th className="text-left px-4 py-3 text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                    ID
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                    Summary
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                    Company
                  </th>
                  <th className="text-center px-4 py-3 text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="text-center px-4 py-3 text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                    Priority
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                    Board
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                    Date
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-200 dark:divide-neutral-700">
                {tickets.map((ticket) => (
                  <tr
                    key={ticket.id}
                    className="hover:bg-neutral-50 dark:hover:bg-neutral-700/50 cursor-pointer"
                    onClick={() => {
                      sessionStorage.setItem('adminTicketsReturnUrl', window.location.pathname + window.location.search);
                      window.location.href = `/tickets/${ticket.id}?from=admin`;
                    }}
                  >
                    <td className="px-4 py-3">
                      <Link
                        to={`/tickets/${ticket.id}?from=admin`}
                        className="font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
                        onClick={(e) => e.stopPropagation()}
                      >
                        #{ticket.id}
                      </Link>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-neutral-900 dark:text-white truncate block max-w-[300px]">
                        {ticket.summary}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-neutral-400" />
                        <span className="text-neutral-900 dark:text-white truncate max-w-[150px]">
                          {ticket.company?.name || '-'}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {getStatusBadge(ticket)}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {getPriorityBadge(ticket)}
                    </td>
                    <td className="px-4 py-3 text-neutral-600 dark:text-neutral-300">
                      {ticket.board?.name || '-'}
                    </td>
                    <td className="px-4 py-3 text-neutral-600 dark:text-neutral-300">
                      {formatDate(ticket.dateEntered)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-neutral-600 dark:text-neutral-400">
            Showing {((pagination.page - 1) * pagination.pageSize) + 1} to{' '}
            {Math.min(pagination.page * pagination.pageSize, pagination.total)} of{' '}
            {pagination.total} tickets
          </p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => handlePageChange(1)}
              disabled={pagination.page <= 1}
              className="p-2 rounded-lg border border-neutral-300 dark:border-neutral-600 hover:bg-neutral-50 dark:hover:bg-neutral-700 disabled:opacity-50 disabled:cursor-not-allowed dark:text-neutral-300"
              title="First page"
            >
              <ChevronsLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page <= 1}
              className="p-2 rounded-lg border border-neutral-300 dark:border-neutral-600 hover:bg-neutral-50 dark:hover:bg-neutral-700 disabled:opacity-50 disabled:cursor-not-allowed dark:text-neutral-300"
              title="Previous page"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-sm text-neutral-600 dark:text-neutral-400 px-3">
              Page {pagination.page} of {pagination.pages}
            </span>
            <button
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={pagination.page >= pagination.pages}
              className="p-2 rounded-lg border border-neutral-300 dark:border-neutral-600 hover:bg-neutral-50 dark:hover:bg-neutral-700 disabled:opacity-50 disabled:cursor-not-allowed dark:text-neutral-300"
              title="Next page"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => handlePageChange(pagination.pages)}
              disabled={pagination.page >= pagination.pages}
              className="p-2 rounded-lg border border-neutral-300 dark:border-neutral-600 hover:bg-neutral-50 dark:hover:bg-neutral-700 disabled:opacity-50 disabled:cursor-not-allowed dark:text-neutral-300"
              title="Last page"
            >
              <ChevronsRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
